import React, { useEffect, useContext } from 'react';
import { Typography, Grid, Card, CardContent, Box, Chip, CircularProgress, Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { EcrDeploymentsContext } from '../contexts/EcrDeploymentsContext';
import StatsCard from '../components/StatsCard';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ComputerIcon from '@mui/icons-material/Computer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';

const useStyles = makeStyles({
  header: {
    marginBottom: 24,
  },
  serviceCard: {
    height: '100%',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    },
  },
  serviceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  serviceName: {
    fontWeight: 600,
    fontSize: '1.1rem',
  },
  versionText: {
    fontSize: '0.875rem',
    color: '#666',
    marginBottom: 4,
  },
  statsGrid: {
    marginTop: 16,
    paddingTop: 16,
    borderTop: '1px solid #e0e0e0',
  },
  statItem: {
    textAlign: 'center',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
});

const DeploymentDashboard: React.FC = () => {
  const classes = useStyles();
  const { deploymentStats, loading, error, fetchDeploymentStats } = useContext(EcrDeploymentsContext);

  useEffect(() => {
    fetchDeploymentStats();
  }, []);

  if (loading && !deploymentStats) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!deploymentStats) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No deployment data available
      </Alert>
    );
  }

  const { services, totals } = deploymentStats;

  return (
    <>
      <Box className={classes.header}>
        <Typography variant="h1" component="h1" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <RocketLaunchIcon style={{ fontSize: 36 }} />
          Deployment Dashboard
        </Typography>
        <Typography variant="body1" color="textSecondary" style={{ marginTop: 8 }}>
          Monitor ECR deployment status across all services and machines
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} style={{ marginBottom: 32 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Releases"
            value={totals.total_releases}
            color="#1976d2"
            subtitle="Across all services"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Active Machines"
            value={totals.total_machines_with_deployments}
            color="#2e7d32"
            subtitle="With deployments"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Active Assignments"
            value={totals.total_active_assignments}
            color="#ed6c02"
            stats={[
              { label: 'Completed', value: totals.total_completed, color: '#2e7d32' },
              { label: 'Pending', value: totals.total_pending, color: '#d32f2f' },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Success Rate"
            value={`${totals.total_active_assignments > 0 ? Math.round((totals.total_completed / totals.total_active_assignments) * 100) : 0}%`}
            color={totals.total_pending > 0 ? '#ed6c02' : '#2e7d32'}
            chip={
              totals.total_pending > 0
                ? { label: `${totals.total_pending} Pending`, color: 'warning' }
                : { label: 'All Complete', color: 'success' }
            }
          />
        </Grid>
      </Grid>

      {/* Services Section */}
      <Typography variant="h5" style={{ fontWeight: 600, marginBottom: 16 }}>
        Service Status
      </Typography>

      <Grid container spacing={3}>
        {services.map((service) => (
          <Grid item xs={12} md={6} lg={4} key={service.service_name}>
            <Card className={classes.serviceCard}>
              <CardContent>
                <Box className={classes.serviceHeader}>
                  <Typography className={classes.serviceName}>{service.service_name}</Typography>
                  <Chip
                    label={`${service.total_releases} releases`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>

                {service.last_release.version ? (
                  <>
                    <Typography className={classes.versionText}>
                      <strong>Latest:</strong> {service.last_release.version}
                    </Typography>
                    <Typography className={classes.versionText}>
                      <strong>Deployed:</strong>{' '}
                      {service.last_release.deployed_at
                        ? new Date(service.last_release.deployed_at).toLocaleString()
                        : 'N/A'}
                    </Typography>
                  </>
                ) : (
                  <Typography className={classes.versionText} style={{ color: '#999' }}>
                    No releases yet
                  </Typography>
                )}

                <Grid container spacing={2} className={classes.statsGrid}>
                  <Grid item xs={4} className={classes.statItem}>
                    <ComputerIcon style={{ color: '#1976d2', fontSize: 28, marginBottom: 4 }} />
                    <Typography style={{ fontWeight: 600, fontSize: '1.25rem' }}>
                      {service.machines_with_active_deployment}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Machines
                    </Typography>
                  </Grid>
                  <Grid item xs={4} className={classes.statItem}>
                    <CheckCircleIcon style={{ color: '#2e7d32', fontSize: 28, marginBottom: 4 }} />
                    <Typography style={{ fontWeight: 600, fontSize: '1.25rem', color: '#2e7d32' }}>
                      {service.deployments_completed}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Completed
                    </Typography>
                  </Grid>
                  <Grid item xs={4} className={classes.statItem}>
                    <PendingIcon style={{ color: '#ed6c02', fontSize: 28, marginBottom: 4 }} />
                    <Typography style={{ fontWeight: 600, fontSize: '1.25rem', color: '#ed6c02' }}>
                      {service.deployments_pending}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Pending
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default DeploymentDashboard;
